import React, { useEffect, useState, useRef } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import CloseablePopup from '../components/closeable_popup';
import { NotificationBellIcon } from '../icons/icons';
import { GetAppSettings } from '../AppSettings';
import { CreateConnection } from './NotificationHub';
import { apiCall, safeConcatUrl } from '../helpers/api';
import NotificationItemOld, { ResourceTypes } from './NotificationItemOld';
import isEqual from 'lodash.isequal';
import './NotificationCenterOld.scss';
import { reloadProfile } from '../profile/actions';
import { NotificationData } from './notifications-types';
import { useAppSelector } from '../hooks/ReduxHooks';
import { NOTIFICATIONCENTER_TEMPLATECOPIED, markAsRead, openNotificationCenter, updateNotifications } from './actions';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

export function NotificationCenterOld() {
  const notifications = useAppSelector<NotificationData[]>((state) => state.notificationCenter.notificationDataList, isEqual);
  const { notificationCenterOpen } = useAppSelector((state) => state.notificationCenter);
  const [loadingText, setLoadingText] = useState(null);
  const popup = useRef<TypeMePlease>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const appSettings = GetAppSettings();
    const connection = CreateConnection();

    const checkIfTemplateCopied = (notificationsToCheck) => {
      for (const notification of notificationsToCheck) {
        if (notification.completed && notification.link && notification.link.resourceType === ResourceTypes.Template) {
          dispatch({ type: NOTIFICATIONCENTER_TEMPLATECOPIED });
          break;
        }
      }
    };

    const loadNotificationsFromServer = async () => {
      setLoadingText('Loading...');
      const notificationResult = await apiCall(safeConcatUrl(appSettings.NotificationServiceUrl, 'notifications'));

      //Filter notifications that are unread or read within the last hour. This was originally done in the backend before the new notification center was implemented.
      const filteredNotifications = notificationResult.data.filter(
        (x: NotificationData) => !x.read || new Date(x.timestamp).getTime() > new Date().getTime() - 3600000
      );

      dispatch(updateNotifications(filteredNotifications));
      checkIfTemplateCopied(filteredNotifications);
      setLoadingText(null);
    };

    setLoadingText('Connecting...');
    connection.start().then(async () => {
      await loadNotificationsFromServer();
    });

    const onReceiveNotification = (notification) => {
      dispatch(updateNotifications([notification]));
      checkIfTemplateCopied([notification]);
    };

    const onReceiveIntegrationEvent = (integrationEvent) => {
      if (
        integrationEvent.eventType === 'AccountMaintenance' ||
        integrationEvent.eventType === 'AccountFeatures' ||
        integrationEvent.eventType === 'AccountSettings'
      ) {
        reloadProfile()(dispatch);
      }
    };

    const onReconnected = () => {
      loadNotificationsFromServer();
    };

    connection.on('ReceiveNotification', onReceiveNotification);
    connection.on('ReceiveIntegrationEvent', onReceiveIntegrationEvent);
    connection.onreconnected(onReconnected);

    return () => {
      connection.off('ReceiveNotification', onReceiveNotification);
      connection.off('ReceiveIntegrationEvent', onReceiveIntegrationEvent);
    };
  }, [dispatch, notifications]);

  const hasUnread = notifications.some((x) => !x.read);

  const notificationCenterMount = () => {
    dispatch(openNotificationCenter(true));
  };

  const notificationCenterUnmount = () => {
    dispatch(openNotificationCenter(false));
    markAsRead(notifications, dispatch);
  };

  const handleNotificationCenterOpen = (isOpen: boolean) => () => {
    dispatch(openNotificationCenter(isOpen));
  };

  const close = () => {
    popup.current.close();
  };

  const notificationCenterRows = [];
  const sortedNotifications =
    notifications.length > 0
      ? [...notifications].sort((a, b) => {
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        })
      : [];

  for (const notification of sortedNotifications) {
    notificationCenterRows.push(<NotificationItemOld key={notification.id} notification={notification} closeNotificationCenter={close} />);
  }

  if (loadingText) {
    notificationCenterRows.push(
      <div key="loading" className="loading-text">
        {loadingText}
      </div>
    );
  } else if (notificationCenterRows.length === 0) {
    notificationCenterRows.push(
      <div key="empty" className="empty-state">
        No recent notifications
      </div>
    );
  }

  const notificationCenter = (
    <div className="notification-center-popup-old">
      <Icon name="caret up" />
      <div className="notification-header-old">Notifications</div>
      {notificationCenterRows}
    </div>
  );

  return (
    <CloseablePopup
      key="popup"
      ref={(c) => {
        popup.current = c;
      }}
      trigger={
        <Button
          className={classNames('notification-center-button', { unread: hasUnread, open: notificationCenterOpen })}
          onClick={handleNotificationCenterOpen(true)}
          id="notification-center"
          tabIndex={0}
        >
          <NotificationBellIcon />
        </Button>
      }
      content={notificationCenter}
      on="click"
      basic
      position="bottom right"
      style={{ padding: '0px' }}
      closeOnDocumentClick
      // openOnTriggerFocus
      onMount={notificationCenterMount}
      onUnmount={notificationCenterUnmount}
      open={notificationCenterOpen}
      onClose={handleNotificationCenterOpen(false)}
    />
  );
}
